import { useState } from 'react';
import { multiplierFor, type PointsBand, type PointsMultiplier } from './definition';
import {
  IssueList,
  Labelled,
  NumberInput,
  Worked,
  controlClass,
  smallButtonClass,
} from './controls';
import { issuesFor, type DefinitionValidation, type IssueSection } from './validate';
import { METRIC_UNIT, multiplierExample, trimNumber, type Choice } from './wording';

/**
 * One banded weighting — field size or buy-in. Both are the same control over a different metric,
 * and both are optional in the same way: **absent is not a ladder that happens to yield 1.0
 * everywhere.** Switching this off removes the weighting from the definition entirely, which is
 * what SHRPO does with buy-in on purpose: a $300 event and a $25,500 High Roller award identical
 * base points there.
 *
 * The cap and floor clamp the metric *before* the lookup, which is the whole point of having
 * them: one 5,000-entry event next to a 35-entry one would otherwise decide a season on its own.
 */
export function MultiplierEditor<TMetric extends string>({
  section,
  value,
  onChange,
  metrics,
  defaults,
  subject,
  switchLabel,
  switchDetail,
  validation,
  readOnly = false,
}: {
  section: Extract<IssueSection, 'fieldMultiplier' | 'buyInMultiplier'>;
  value: PointsMultiplier<TMetric> | null;
  onChange: (next: PointsMultiplier<TMetric> | null) => void;
  metrics: readonly Choice<TMetric>[];
  /** What switching it on starts from. A ladder to edit beats an empty table. */
  defaults: () => PointsMultiplier<TMetric>;
  /** "field size" / "buy-in" — reads inside sentences. */
  subject: string;
  switchLabel: string;
  switchDetail: string;
  validation: DefinitionValidation;
  readOnly?: boolean;
}) {
  const on = value !== null;

  return (
    <div className="space-y-3">
      <label className={`flex gap-2.5 ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          checked={on}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.checked ? defaults() : null)}
          className="mt-0.5 accent-[var(--color-felt-700)]"
        />
        <span className="min-w-0">
          <span className="block text-[13px] font-medium text-ink-950">{switchLabel}</span>
          <span className="mt-0.5 block text-[11.5px] leading-relaxed text-ink-800/60">
            {switchDetail}
          </span>
        </span>
      </label>

      {value ? (
        <Bands
          section={section}
          value={value}
          onChange={onChange}
          metrics={metrics}
          subject={subject}
          validation={validation}
          readOnly={readOnly}
        />
      ) : null}
    </div>
  );
}

function Bands<TMetric extends string>({
  section,
  value,
  onChange,
  metrics,
  subject,
  validation,
  readOnly,
}: {
  section: Extract<IssueSection, 'fieldMultiplier' | 'buyInMultiplier'>;
  value: PointsMultiplier<TMetric>;
  onChange: (next: PointsMultiplier<TMetric>) => void;
  metrics: readonly Choice<TMetric>[];
  subject: string;
  validation: DefinitionValidation;
  readOnly: boolean;
}) {
  const metric = metrics.find((m) => m.value === value.metric);
  const unit = METRIC_UNIT[value.metric as keyof typeof METRIC_UNIT] ?? subject;

  // Seeded from the middle of the operator's own ladder, so the first thing they see is an
  // example about their own room rather than a number this file made up.
  const [sample, setSample] = useState<number | null>(() => seedSample(value.bands));

  const lastIndex = value.bands.length - 1;
  const setBands = (bands: PointsBand[]) => onChange({ ...value, bands });
  const setBand = (index: number, patch: Partial<PointsBand>) =>
    setBands(value.bands.map((band, i) => (i === index ? { ...band, ...patch } : band)));

  const addBand = () => {
    const last = value.bands[lastIndex];
    const nextFrom = last ? ((last.to ?? last.from ?? 0) + 1) : 0;
    setBands([...value.bands, { from: nextFrom, to: nextFrom, value: last?.value ?? 1 }]);
  };

  const openEnded = value.bands.length > 0 && value.bands[lastIndex]!.to === null;

  return (
    <div className="space-y-3 border-l-2 border-line pl-4">
      <Labelled label={`Weight by`}>
        <select
          value={value.metric}
          disabled={readOnly}
          onChange={(e) => onChange({ ...value, metric: e.target.value as TMetric })}
          className={`${controlClass} w-full max-w-xs`}
        >
          {metrics.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </Labelled>
      {metric ? <p className="text-[11.5px] text-ink-800/55">{metric.detail}</p> : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[22rem] text-left text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
              <th className="w-[55%] pb-1.5 font-semibold">{capitalise(unit)}</th>
              <th className="pb-1.5 font-semibold">Multiplier</th>
              <th className="w-8 pb-1.5" />
            </tr>
          </thead>
          <tbody>
            {value.bands.map((band, index) => {
              const bad = issuesFor(validation, section, index).some((i) => i.severity === 'error');
              const isLast = index === lastIndex;

              return (
                <tr key={index} className="align-top">
                  <td className="py-1 pr-2">
                    <div className="flex items-center gap-1.5">
                      <NumberInput
                        value={band.from}
                        onChange={(from) => setBand(index, { from })}
                        ariaLabel={`Band ${index + 1}: from`}
                        disabled={readOnly}
                        invalid={bad}
                        className="w-20"
                      />
                      <span className="text-xs text-ink-800/40">to</span>
                      {isLast && openEnded ? (
                        <span className="text-xs text-ink-800/55">and up</span>
                      ) : (
                        <NumberInput
                          value={band.to}
                          onChange={(to) => setBand(index, { to })}
                          ariaLabel={`Band ${index + 1}: to`}
                          disabled={readOnly}
                          invalid={bad}
                          className="w-20"
                        />
                      )}
                    </div>
                  </td>
                  <td className="py-1 pr-2">
                    <NumberInput
                      value={band.value}
                      onChange={(v) => setBand(index, { value: v })}
                      ariaLabel={`Band ${index + 1}: multiplier`}
                      min={0}
                      step={0.1}
                      disabled={readOnly}
                      invalid={bad}
                      className="w-20"
                    />
                  </td>
                  <td className="py-1">
                    {!readOnly && value.bands.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setBands(value.bands.filter((_, i) => i !== index))}
                        aria-label={`Remove band ${index + 1}`}
                        className="px-1 text-sm text-ink-800/40 transition hover:text-bad"
                      >
                        ×
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <IssueList issues={issuesFor(validation, section)} />

      {!readOnly ? (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={addBand} className={smallButtonClass}>
            Add band
          </button>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-800/70">
            <input
              type="checkbox"
              checked={openEnded}
              disabled={value.bands.length === 0}
              onChange={(e) => {
                const last = value.bands[lastIndex];
                if (last) setBand(lastIndex, { to: e.target.checked ? null : last.from });
              }}
              className="accent-[var(--color-felt-700)]"
            />
            Last band is open-ended
          </label>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <Labelled
          label={`Ignore anything below`}
          hint={`Anything smaller is treated as this. Leave empty for no floor.`}
        >
          <NumberInput
            value={value.floor}
            onChange={(floor) => onChange({ ...value, floor })}
            ariaLabel={`Smallest ${subject} counted`}
            disabled={readOnly}
            className="w-full"
            placeholder="No floor"
          />
        </Labelled>
        <Labelled
          label={`Count no more than`}
          hint={`Stops one enormous event deciding the season. Leave empty for no cap.`}
        >
          <NumberInput
            value={value.cap}
            onChange={(cap) => onChange({ ...value, cap })}
            ariaLabel={`Largest ${subject} counted`}
            disabled={readOnly}
            className="w-full"
            placeholder="No cap"
          />
        </Labelled>
      </div>

      {/* §9.5: every numeric input gets a worked example. This one is live, and it is computed
          from the ladder above — including the clamp — so it is a reading of what the operator
          has built rather than a claim about what any room pays. */}
      <div className="flex flex-wrap items-end gap-3">
        <Labelled label="Try a value" className="w-32">
          <NumberInput
            value={sample}
            onChange={setSample}
            ariaLabel={`Sample ${subject}`}
            className="w-full"
          />
        </Labelled>
        <div className="min-w-0 flex-1 pb-1.5">
          {sample === null ? (
            <Worked>Type a {subject} to see what an event that size would score.</Worked>
          ) : (
            <Worked>
              {multiplierExample(
                value.metric as keyof typeof METRIC_UNIT,
                sample,
                multiplierFor(value, sample),
              )}
              {clampNote(value, sample)}
            </Worked>
          )}
        </div>
      </div>
    </div>
  );
}

/** Says so when the cap or floor moved the value before the lookup, rather than leaving it a mystery. */
function clampNote(multiplier: PointsMultiplier<string>, sample: number): string {
  if (multiplier.floor !== null && sample < multiplier.floor) {
    return ` Counted as ${trimNumber(multiplier.floor)} — anything below that is treated the same.`;
  }
  if (multiplier.cap !== null && sample > multiplier.cap) {
    return ` Counted as ${trimNumber(multiplier.cap)} — the cap stops a huge event running away with it.`;
  }
  return '';
}

/** The middle band's starting value: the most representative single number the ladder offers. */
function seedSample(bands: readonly PointsBand[]): number | null {
  if (bands.length === 0) return null;
  return bands[Math.floor(bands.length / 2)]!.from;
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
